/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useEffect, useState} from "react";
import {UsersModule} from "@modules/UsersModule";
import {AlertsModule} from "@modules/AlertsModule";
import {OrganizationsModule} from "@modules/OrganizationsModule";
import {CommentsModule} from "@modules/CommentsModule";
import {ContactsModule} from "@modules/ContactsModule";
import {PatientsModule} from "@modules/PatientsModule";
import {PatientsContext, PatientsMap} from "./pages/ui/patients/PatientsContext";
import {DB_Patient} from "@app/app-shared/patient";
import {PartialProperties} from "@intuitionrobotics/ts-common";
import {Loader} from "../widgets/Loader";
import {ChatsContext, ChatsMap, isSendingToChatMap, MessagesMap} from "./pages/ui/messages/ChatsContext";
import {CCInbox, MessageWithStatus} from "@app/ir-q-app-common/types/message-api";
import {upsertImpl} from "@modules/utils";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {OrganizationMap, OrganizationsContext} from "./pages/ui/organizations/OrganizationsContext";
import {DB_Alert, PushKey_NewAlert} from "@app/app-shared/alert";
import {PushPubSubModule} from "@intuitionrobotics/push-pub-sub/frontend";
import {ContactsContext, ContactsMap} from "./pages/ui/messages/ContactsContext";
import {DB_Contact} from "@app/ir-q-app-common/types/db-contact";
import {PatientsDataModule} from "@modules/PatientDataModule";
import {DemoDataUtils} from "../demo/Patients_DemoData";
import {GoalsConfigModule} from "@modules/GoalsConfigModule";
import {RemindersModule} from "@modules/RemindersModule";

export const AppContextProvider = ({children}: { children: React.ReactNode }) => {
    const [patients, setPatients] = useState<PatientsMap | undefined>();
    const [patientIds, setPatientIds] = useState<Set<string>>(new Set());
    const [messages, setMessages] = useState<MessagesMap | undefined>();
    const [chats, setChats] = useState<ChatsMap | undefined>();
    const [chatsAlreadyInitialized, setChatsAlreadyInitialized] = useState<boolean>(false);
    const [isSendingToChat, setSendingToChat] = useState<isSendingToChatMap>({});
    const [orgs, setOrgs] = useState<OrganizationMap | undefined>();
    const [contacts, setContacts] = useState<ContactsMap>({});
    const [demo, setDemo] = useState(false);

    const queryAndPopulatePatients = () => {
        PatientsModule
            .querySync()
            .then(p => {
                const pMap: PatientsMap = {};
                const pIds: Set<string> = new Set();
                p.forEach(patient => {
                    pMap[patient._id] = patient;
                    pIds.add(patient._id);
                });
                setPatients(pMap);
                setPatientIds(pIds);
            })
            .catch(console.error);
    };

    useEffect(() => {
        queryAndPopulatePatients();
        OrganizationsModule.querySync().then(fetchedOrgs => {
            saveOrganizations(fetchedOrgs);

            PushPubSubModule.subscribeMulti(fetchedOrgs.map(o => ({
                pushKey: PushKey_NewAlert,
                props: {organizationId: o._id},
                callBack: (alerts: DB_Alert<any>[]) => AlertsModule.onQueryReturned(alerts).catch(console.error)
            })));
        }).catch(console.error);
        UsersModule.query();
        AlertsModule.query();
        CommentsModule.query();
        ContactsModule.queryContacts().then(response => saveContacts(response)).catch(console.error);
    }, []);

    const deletePatient = async (patientId: string) => {
        await PatientsModule.deleteSync(patientId);
        patients && delete patients[patientId];
        setPatients({...patients});
        patientIds.delete(patientId);
        setPatientIds(patientIds);
    }

    const createPatient = async (patient: PartialProperties<DB_Patient, "_id">) => {
        const {_id: pId} = await PatientsModule.createSync(patient);
        setPatients({...patients, [pId]: {...patient, _id: pId}});
        setPatientIds(patientIds.add(pId));
    }

    const saveMessages = (_messages: MessageWithStatus[]) => {
        setMessages(prevMessages => ({
            ..._messages.reduce((carry: { [chatId: string]: MessageWithStatus[] }, msg) => {
                carry[msg.chatId] = carry[msg.chatId] || [];
                carry[msg.chatId].push(msg);
                return carry;
            }, prevMessages || {})
        }));
    }

    const updateMessages = (_messages: MessageWithStatus[]) => {
        setMessages(prevMessages => ({
            ..._messages.reduce((carry: { [chatId: string]: MessageWithStatus[] }, msg) => {
                carry[msg.chatId] = upsertImpl((carry[msg.chatId] || []).filter(_m => !_m.draft), msg);
                return carry;
            }, prevMessages || {})
        }));
    }

    const saveChats = (_chats: CCInbox[] | undefined) => {
        if (!_chats)
            return;

        setChats((prevChats) => ({
            ..._chats.reduce((carry: ChatsMap, chat) => {
                carry[chat._id] = chat;
                return carry;
            }, prevChats || {})
        }));
    }

    const saveOrganizations = (_orgs: DB_Organization[]) => {
        setOrgs((prevOrgs) => ({
            ..._orgs.reduce((carry: OrganizationMap, org) => {
                carry[org._id] = org;
                return carry;
            }, prevOrgs || {})
        }));
    }

    const deleteOrganizations = (orgId: string) => {
        setOrgs((prevOrgs) => {
            delete prevOrgs?.[orgId];
            return {...prevOrgs};
        });

        PushPubSubModule.unsubscribe({pushKey: PushKey_NewAlert, props: {organizationId: orgId}});
    }

    const saveContacts = (contacts: DB_Contact<any>[]) => {
        setContacts((prevContacts) => ({
            ...contacts.reduce((carry: ContactsMap, contact) => {
                carry[contact._id] = contact;
                return carry;
            }, prevContacts)
        }));
    }

    const populateDemoData = () => {
        const generateDemoPatients = DemoDataUtils.generateDemoPatients(10, Object.keys(orgs || {}));
        setPatients(generateDemoPatients.reduce((map: PatientsMap, p) => ({...map, [p._id]: p}), {}));
        const pIds = generateDemoPatients.map(p => p._id);
        setPatientIds(new Set(pIds));
        AlertsModule.setDemoAlerts(pIds);
        PatientsDataModule.setData(DemoDataUtils.getPatientData(generateDemoPatients));
        const {chats: _chats, messagesToAdd} = DemoDataUtils.getFakeChats(generateDemoPatients, orgs);
        setChats(_chats);
        saveMessages(messagesToAdd)
        setContacts(DemoDataUtils.getFakeContacts(generateDemoPatients, orgs))
        GoalsConfigModule.setCareGoals(DemoDataUtils.getFakeGoals(generateDemoPatients, orgs));
        RemindersModule.setPatientReminders(DemoDataUtils.getPatientReminders(generateDemoPatients, orgs))
    };

    const resetAfterDemo = () => {
        setPatients({});
        setPatientIds(new Set());
        setChats({});
        setMessages({});
        setContacts({});
        queryAndPopulatePatients();
        AlertsModule.resetAlerts();
        PatientsDataModule.setData({});
        GoalsConfigModule.setCareGoals({});
        RemindersModule.setPatientReminders({});
    };

    const _setDemo = (_demo: boolean) => {
        setDemo(_demo);
        if (_demo) {
            populateDemoData();
        } else {
            resetAfterDemo();
        }
    }

    const setIsSendingToChat = (key: string, value: boolean) => {
        setSendingToChat((prevIsSendingToChat) => ({...prevIsSendingToChat, [key]: value}));
    }

    if (!orgs)
        return <Loader/>

    if (!patients)
        return <Loader/>

    return <OrganizationsContext.Provider value={{organizations: orgs, saveOrganizations, deleteOrganizations}}>
        <ContactsContext.Provider value={{contacts, saveContacts}}>
            <ChatsContext.Provider
                value={{
                    chats,
                    messages,
                    saveMessages,
                    updateMessages,
                    saveChats,
                    alreadyInitialized: chatsAlreadyInitialized,
                    setAlreadyInitialized: () => setChatsAlreadyInitialized(true),
                    isSendingToChat,
                    setIsSendingToChat
                }}>
                <PatientsContext.Provider value={{patients, patientIds: [...patientIds], delete: deletePatient, create: createPatient, setDemo: _setDemo, demo}}>
                    {children}
                </PatientsContext.Provider>
            </ChatsContext.Provider>
        </ContactsContext.Provider>
    </OrganizationsContext.Provider>

}
